import { Operator } from '../../../components/Module/Table/Filters/FilterInput/constants';
import { WithOptional } from '../../../utils/typeUtils';
import { FilterSearchParams } from './FilterSearchParams';
import { FilterParam } from './types';

export interface IFilterSearchGroups {
  deleteAndParams(params: WithOptional<FilterParam, 'value'>[]): void;
  addAndParams(params: FilterParam[]): void;
  addGroup(params: FilterParam[]): void;
  deleteGroup(groupIndex: number): void;
  is(value: FilterParam[][]): boolean;
  includes(value: FilterParam[][]): boolean;
  deleteGroupParam(
    groupIndex: number,
    field: string,
    value?: string,
    operator?: Operator,
  ): void;
  setGroupParam(
    groupIndex: number,
    field: string,
    value: string,
    operator?: Operator,
  ): void;
  appendGroupParam(
    groupIndex: number,
    field: string,
    value: string,
    operator?: Operator,
  ): void;
}

export class FilterSearchGroups implements IFilterSearchGroups {
  private _andParams: FilterParam[];
  private _orParams: FilterParam[][];

  constructor(initialParams: FilterParam[][], andParams?: FilterParam[]) {
    this._orParams = initialParams;
    this._andParams = andParams || [];
  }

  private get value() {
    const groups = this._orParams.filter((v) => v.length > 0);

    if (groups.length > 0) return groups.map((v) => [...v, ...this._andParams]);

    return [[...this._andParams]];
  }

  deleteParams = (params: WithOptional<FilterParam, 'value'>[]) => {
    this.deleteAndParams(params);

    const orParams = [];

    for (let value of this._orParams) {
      const filterSearchParams = new FilterSearchParams(value);

      params.forEach(({ field, value, operator }) => {
        filterSearchParams.delete(field, value, operator);
      });

      if (filterSearchParams.size > 0) {
        orParams.push([...filterSearchParams.entries()]);
      }
    }

    this._orParams = orParams;
  };

  /** Удаляет параметры, которые будут применены в каждой группе. */
  deleteAndParams = (params: WithOptional<FilterParam, 'value'>[]): void => {
    const filterSearchParams = new FilterSearchParams(this._andParams);

    params.forEach(({ field, value, operator }) => {
      filterSearchParams.delete(field, value, operator);
    });

    this._andParams = [...filterSearchParams.entries()];
  };

  /**
   * (Логическое И)
   *
   * Добавляет параметры в коллекцию, которые будут применены в каждой группе.
   *
   * AND параметры автоматически применяются ко всем группам фильтров. Если будет создана новая группа фильтров, то AND параметры будут применены и к ней.
   */
  addAndParams = (params: FilterParam[]): void => {
    this.deleteAndParams(params);

    this._andParams = [...this._andParams, ...params];
  };

  /** Устанавливает новое значение для параметров, которые будут влиять на выборку каждой группы */
  setAndParams = (params: FilterParam[]) => {
    this._andParams = params;
  };

  /**
   * Создаёт новую группу фильтров с параметрами переданными в аргументе `value`.
   *
   * Если требуется манипулировать параметрами в уже существующей группе - воспользуйтесь методом `getGroup(index).[method]`
   */
  addGroup = (params: FilterParam[]): void => {
    this._orParams.push(params);
  };

  setGroups = (params: FilterParam[][]) => {
    this._orParams = params;
  };

  /** Удаляет группу фильтров */
  deleteGroup = (groupIndex: number): void => {
    this._orParams.splice(groupIndex, 1);
  };

  deleteGroupParam = (
    groupIndex: number,
    field: string,
    value?: string,
    operator?: Operator,
  ): void => {
    const params = this._orParams[groupIndex];

    if (!params) return;

    const filterSearchParams = new FilterSearchParams(params);

    filterSearchParams.delete(field, value, operator);

    this._orParams.splice(groupIndex, 1, [...filterSearchParams.entries()]);
  };

  setGroupParam = (
    groupIndex: number,
    field: string,
    value: string,
    operator?: Operator,
  ): void => {
    const params = this._orParams[groupIndex];

    if (!params) return;

    const filterSearchParams = new FilterSearchParams(params);

    filterSearchParams.set(field, value, operator);

    this._orParams.splice(groupIndex, 1, [...filterSearchParams.entries()]);
  };

  appendGroupParam = (
    groupIndex: number,
    field: string,
    value: string,
    operator?: Operator,
  ): void => {
    const params = this._orParams[groupIndex];

    if (!params) return;

    const filterSearchParams = new FilterSearchParams(params);

    filterSearchParams.append(field, value, operator);

    this._orParams.splice(groupIndex, 1, [...filterSearchParams.entries()]);
  };

  /** Возвращает первый найденный фильтр, соответствущий переданным параметрам */
  get = (field: string, operator?: Operator): FilterParam | undefined => {
    for (let value of this.value) {
      const filterSearchParams = new FilterSearchParams(value);

      const result = filterSearchParams.get(field, operator);

      if (result) return result;
    }

    return;
  };

  /** Возвращает фильтры, соответствущие переданным параметрам, сохраняя группы */
  getAll = (field: string, operator?: Operator): FilterParam[][] => {
    const result: FilterParam[][] = [];

    for (let value of this.value) {
      const filterSearchParams = new FilterSearchParams(value);

      const params = filterSearchParams.getAll(field, operator);

      if (params.length > 0) result.push(params);
    }

    return result;
  };

  /** Входит ли `value` в текущие параметры фильтрации. Проверяет по значению */
  includes = (value: FilterParam[][]) => {
    if (value.length > this.value.length) {
      return false;
    }

    const sortedThis = [...this.value].map((group) => [...group]);
    const sortedValue = [...value].map((group) => [...group]);

    sortedThis.forEach(this._sortGroup);
    sortedValue.forEach(this._sortGroup);

    sortedThis.sort(this._compareObjects);
    sortedValue.sort(this._compareObjects);

    return sortedValue.every((valueGroup) => {
      return sortedThis.some((thisGroup) => {
        const internal = new FilterSearchParams(thisGroup);

        return internal.includes(valueGroup);
      });
    });
  };

  /** Входит ли value в текущие параметры фильтрации */
  is = (value: FilterParam[][]) => {
    if (this.value.length !== value.length) {
      return false;
    }

    const sortedThis = [...this.value].map((group) => [...group]);
    const sortedValue = [...value].map((group) => [...group]);

    sortedThis.forEach(this._sortGroup);
    sortedValue.forEach(this._sortGroup);

    sortedThis.sort(this._compareObjects);
    sortedValue.sort(this._compareObjects);

    for (let i = 0; i < sortedThis.length; i++) {
      const currentGroup = sortedThis[i];
      const compareGroup = sortedValue[i];

      if (currentGroup === undefined || compareGroup === undefined) {
        return false;
      }

      if (currentGroup.length !== compareGroup.length) {
        return false;
      }

      for (let j = 0; j < currentGroup.length; j++) {
        const currentParam = currentGroup[j];
        const compareParam = compareGroup[j];

        if (currentParam === undefined || compareParam === undefined) {
          return false;
        }

        if (
          currentParam.field !== compareParam.field ||
          currentParam.operator !== compareParam.operator ||
          currentParam.value !== compareParam.value
        ) {
          return false;
        }
      }
    }

    return true;
  };

  *entries(): IterableIterator<IterableIterator<FilterParam>> {
    for (const group of this.value) {
      const filterSearchParams = new FilterSearchParams([...group]);

      yield filterSearchParams.entries();
    }
  }

  toString = (): string => {
    const filterSearchParams = this.value.map((v) => new FilterSearchParams(v));

    let i = 0;

    return filterSearchParams
      .map((v) => {
        i++;

        return v.toString(this.value.length > 1 ? i + '' : undefined);
      })
      .join('&');
  };

  private _sortGroup = (group: FilterParam[]) => {
    return group.sort(this._compareObjects);
  };

  private _compareObjects = (a: object, b: object) => {
    return JSON.stringify(a).localeCompare(JSON.stringify(b));
  };
}
